import type {
  DocumentCatalog,
  DraftWarning,
  StepView,
} from "@/components/application/merchant-tasks/types";

import type { WireChoice } from "../../protocol/protocol";

/**
 * The send card's payload (`choice.card`, kind `merchant_flow`, version 1): F1's
 * `apps.merchant_flow.cards.FlowCard`. Every value is plain JSON; merchant- and staff-written
 * text is a value, never markup.
 */
export const FLOW_CARD_KIND = "merchant_flow";

export type FlowBody = "contract" | "bank" | "documents" | "other";

/** `send.PreviewStep.as_dict`: the merchant's stepper as the card proposes it. */
export interface FlowCardStep {
  key: string;
  type: StepView["type"];
  label: string;
  skipped: boolean;
  skip_reason?: string;
  kind?: string;
  document?: boolean;
  /** Abort-only (a voided check behind Plaid, the ID after it): off the merchant's list. */
  withheld?: boolean;
  /** A later copy of a step already listed: off the list too. */
  omitted?: boolean;
}

/** A step another open flow holds: the merchant does it there, not in this send. */
const IN_FLIGHT_PEER = "in_flight_peer";

export interface FlowTermField {
  key: string;
  label: string;
  value: string | number | null;
  editable: boolean;
  edited: boolean;
}

/** `terms.card_terms`: the calculator, values as plain strings ("1.35", "25000"). */
export interface FlowTerms {
  source: "assessment" | "refinance_offer";
  available: boolean;
  fields: FlowTermField[];
  derived: {
    payback: string | null;
    disbursed: string | null;
    origination_fee_amount: string | null;
    specified_percentage: string | number | null;
  };
}

export interface FlowTemplateRow {
  key: string;
  label: string;
  id: number;
  name: string;
}

export interface FlowRequestedDocument {
  key: string;
  label: string;
  custom: boolean;
}

export interface FlowBank {
  institution: string;
  accounts: string[];
  status: "active" | "login_required" | "revoked" | (string & {});
  connected_at: string;
}

/** One recipe option as configured (`cards._settings`). */
export interface FlowSetting {
  key: string;
  label: string;
  type: string;
  section: string;
  value: unknown;
  default: unknown;
  options: unknown;
  changed: boolean;
}

/** An open flow the send cancels (`cards._replaced_row`). */
export interface FlowReplacedRow {
  merchant_task_id: number;
  flow_type: string;
  label: string;
  status: string;
  created_at: string;
  last_sent_at: string | null;
  current_step: string | null;
}

export interface FlowCardPayload {
  kind: typeof FLOW_CARD_KIND;
  version: 1;
  flow_type: string;
  body: FlowBody;
  title: string;
  application: { id: number; name: string };
  steps: FlowCardStep[];
  terms: FlowTerms | null;
  template: FlowTemplateRow[] | null;
  documents: { requested: FlowRequestedDocument[]; catalog: DocumentCatalog } | null;
  banks: FlowBank[] | null;
  statement_window: { start: string; end: string; days: number } | null;
  message: { value: string; default: string; editable: boolean };
  settings: FlowSetting[];
  warnings: DraftWarning[];
  /** The open flows Send cancels. */
  replaces: FlowReplacedRow[];
  supersedes: { submission_id: number }[];
  recipient: { email: string; sent_as: string };
  preview: { label: string; offered: boolean; required: boolean; staff_fill: boolean } | null;
  sendable: boolean;
  blocked_reason: string | null;
  labels: { approve: string; deny: string; declined?: string };
  /** The keys a press may send back as overrides. */
  overrides: string[];
  /** The send this card makes: posted verbatim to contract-preview to mint its preview. */
  request: Record<string, unknown>;
  /** On the picked fake model (`Tool._card_with_overrides`). */
  fake_model?: boolean;
}

/** The choice's flow card, when it carries one this build understands. */
export function flowCard(choice: WireChoice | undefined | null): FlowCardPayload | null {
  const card = choice?.card;
  if (!card || card.kind !== FLOW_CARD_KIND || card.version !== 1) return null;
  return card as unknown as FlowCardPayload;
}

/** The proposed steps as the app's stepper reads steps: nothing done yet, skipped ones greyed. */
export function proposedSteps(card: FlowCardPayload): StepView[] {
  return card.steps.map((step) => ({
    key: step.key,
    type: step.type,
    kind: step.kind,
    // A step skipped because an open flow holds it is not done: its label says where it is.
    label:
      step.skipped && step.skip_reason === IN_FLIGHT_PEER
        ? `${step.label} · in another open flow`
        : step.label,
    merchant_complete: false,
    fact_complete: false,
    skipped: step.skipped,
    withheld: step.withheld,
    omitted: step.omitted,
  }));
}

export const FEE_KEY = "origination_fee_percentage";

/** What the user changed on the card, before it goes back as the press's overrides. */
export interface FlowEdits {
  fee?: string;
  message?: string;
  documents?: string[];
  settings?: Record<string, unknown>;
}

/** Plain JSON compared by value: an object's keys in any order. */
function sameValue(a: unknown, b: unknown): boolean {
  if (a === b) return true;
  if (!a || !b || typeof a !== "object" || typeof b !== "object") return false;
  if (Array.isArray(a) !== Array.isArray(b)) return false;
  const keys = new Set([...Object.keys(a), ...Object.keys(b)]);
  return [...keys].every((key) =>
    sameValue((a as Record<string, unknown>)[key], (b as Record<string, unknown>)[key])
  );
}

/** The documents the card requests, as a press names them (catalog keys, then custom names). */
export function requestedNames(card: FlowCardPayload): string[] {
  const requested = card.documents?.requested ?? [];
  return [
    ...requested.filter((doc) => !doc.custom).map((doc) => doc.key),
    ...requested.filter((doc) => doc.custom).map((doc) => doc.label),
  ];
}

/** The server takes the fee in steps of 0.25 (`terms.TermsSerializer`). */
export const FEE_STEP = 0.25;

/** A typed fee as the press sends it: snapped to the server's step, as the box does on blur. */
export function settledFee(fee: string): string {
  const n = Number(fee);
  if (!Number.isFinite(n)) return fee;
  const snapped = Math.round(Math.max(0, Math.min(25, n)) / FEE_STEP) * FEE_STEP;
  return snapped % 1 === 0 ? String(snapped) : snapped.toFixed(2);
}

/**
 * A document list as the server stores it (`cards.document_config`): catalog documents as a set
 * (by key, or a name matching a catalog label), other names casefolded once each. Synonyms only
 * the server knows ("statements") it still folds itself, and a press it reads as no change is not
 * sent (`with_overrides`' "Nothing changed").
 */
function storedDocuments(
  names: string[],
  card: FlowCardPayload
): { keys: string[]; custom: string[] } {
  const entries = card.documents?.catalog.document_types ?? [];
  const byKey = new Set(entries.filter((entry) => !entry.custom_label).map((entry) => entry.key));
  // A catalog document by its label or its title ("Bank Statements"), folded.
  const byLabel = new Map(
    entries
      .filter((entry) => !entry.custom_label)
      .flatMap((entry) =>
        [entry.label, entry.title]
          .filter(Boolean)
          .map((name) => [name.toLowerCase(), entry.key] as const)
      )
  );
  const keys = new Set<string>();
  const custom = new Set<string>();
  for (const raw of names) {
    const name = raw.trim();
    if (!name) continue;
    const folded = name.toLowerCase();
    const key = byKey.has(name) ? name : byLabel.get(folded);
    if (key) keys.add(key);
    else custom.add(folded);
  }
  return { keys: [...keys].sort(), custom: [...custom].sort() };
}

/**
 * The overrides of the edits: only those that differ from what the card shows (a chip toggled
 * back, a fee typed back, a tile toggled off and on, are no change), and only keys the card takes
 * back. `settle` (the press) snaps a typed fee to the server's step first, as the box does on
 * blur; without it (what the button shows) a fee reads as typed.
 */
export function flowOverrides(
  card: FlowCardPayload,
  edits: FlowEdits,
  { settle = false }: { settle?: boolean } = {}
): Record<string, unknown> {
  const allowed = new Set(card.overrides);
  const out: Record<string, unknown> = {};
  const fee = card.terms?.fields.find((row) => row.key === FEE_KEY)?.value;
  const typedFee =
    edits.fee !== undefined ? (settle ? settledFee(edits.fee) : edits.fee) : undefined;
  if (typedFee !== undefined && allowed.has("terms") && Number(typedFee) !== Number(fee)) {
    out.terms = { [FEE_KEY]: typedFee };
  }
  if (
    edits.message !== undefined &&
    allowed.has("message") &&
    edits.message !== card.message.value
  ) {
    out.message = edits.message;
  }
  if (
    edits.documents !== undefined &&
    allowed.has("documents") &&
    !sameValue(storedDocuments(edits.documents, card), storedDocuments(requestedNames(card), card))
  ) {
    out.documents = edits.documents;
  }
  if (edits.settings && allowed.has("settings")) {
    const shown = new Map(card.settings.map((setting) => [setting.key, setting.value]));
    const settings = Object.fromEntries(
      Object.entries(edits.settings).filter(([key, value]) => !sameValue(value, shown.get(key)))
    );
    if (Object.keys(settings).length) out.settings = settings;
  }
  return out;
}

/**
 * Whether a press's overrides change the contract (anything in its request but the message):
 * such a press redraws the card with them, and only the next press sends.
 */
export function changesContract(overrides: Record<string, unknown>): boolean {
  return "terms" in overrides || "settings" in overrides || "documents" in overrides;
}

/** "Send" → "Sending": the receipt's verb while the approved send runs. */
export function presentTense(approve: string): string {
  const words: Record<string, string> = {
    Send: "Sending",
    Resend: "Resending",
    Request: "Requesting",
  };
  return words[approve] ?? "Working";
}

/** "Send" → "Sent": the receipt's verb for the card's approve label. */
export function pastTense(approve: string): string {
  const words: Record<string, string> = {
    Send: "Sent",
    Resend: "Resent",
    Request: "Requested",
  };
  return words[approve] ?? `${approve} done`;
}
